/**
 * The two long-form article areas, and the mapping used to turn a stored path
 * (e.g. `/geo/what-is-mention-rate`) back into a typed TanStack route plus the
 * article it points at.
 *
 * Resolution is checked against the real data, not just the path shape: a
 * recommendation whose target has been renamed or moved resolves to `null`
 * instead of rendering a link into a 404.
 */
import { allTutorials } from "./tutorials-data";
import { allGeoArticles } from "./geo-data";
import type { Article } from "./content-blocks";

export const AREA_ROUTES = {
  tutorials: "/tutorials/$slug",
  geo: "/geo/$slug",
} as const;

export type AreaRoute = (typeof AREA_ROUTES)[keyof typeof AREA_ROUTES];
export type AreaBase = "/tutorials" | "/geo";

const ARTICLES_BY_PATH: ReadonlyMap<string, Article> = new Map([
  ...allTutorials.map((a) => [`/tutorials/${a.slug}`, a] as const),
  ...allGeoArticles.map((a) => [`/geo/${a.slug}`, a] as const),
]);

export type ResolvedArticle = { to: AreaRoute; slug: string; article: Article };

/** Resolve `/geo/foo` to a typed route + the target article, or null if no such article exists. */
export function resolveArticlePath(path: string): ResolvedArticle | null {
  const article = ARTICLES_BY_PATH.get(path);
  if (!article) return null;

  const [, area] = path.split("/");
  const to = AREA_ROUTES[area as keyof typeof AREA_ROUTES];
  return to ? { to, slug: article.slug, article } : null;
}

/** Every `next[]` path across both areas that does not resolve to a real article. */
export function danglingArticlePaths(): string[] {
  return [...allTutorials, ...allGeoArticles]
    .flatMap((a) => (a.next ?? []).map((path) => ({ from: a.slug, path })))
    .filter(({ path }) => !resolveArticlePath(path))
    .map(({ from, path }) => `${from} -> ${path}`);
}
